import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge, Box, Card, CardBody, CardHeader, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
  DrawerHeader, DrawerOverlay, Flex, FormControl, FormLabel, Heading, HStack, IconButton, Input,
  Select, SimpleGrid, Spinner, Stat, StatLabel, StatNumber, Switch, Table, Tbody, Td, Text, Th,
  Thead, Tooltip, Tr, VStack, Wrap, WrapItem, useDisclosure,
} from '@chakra-ui/react';
import { FiPause, FiPlay, FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import activityService from '../services/activityService';

// Live panel refresh. Polls only while this tab is visible — superadmin
// only, so the cost is one small request every 15s from one browser.
const LIVE_POLL_MS = 15000;
// A trial hotel at or above this engagement score is flagged as a lead.
const HOT_LEAD_SCORE = 60;

const ago = (seconds, t) => {
  if (seconds == null) return '';
  if (seconds < 60) return t('userActivity.ago.seconds', { value: Math.max(0, seconds) });
  if (seconds < 3600) return t('userActivity.ago.minutes', { value: Math.floor(seconds / 60) });
  if (seconds < 86400) return t('userActivity.ago.hours', { value: Math.floor(seconds / 3600) });
  return t('userActivity.ago.days', { value: Math.floor(seconds / 86400) });
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const HotelCell = ({ hotel }) => {
  const { t } = useTranslation();
  return hotel ? (
    <HStack spacing={2}>
      <Text fontWeight="medium" noOfLines={1}>{hotel.name}</Text>
      {hotel.is_trial && <Badge colorScheme="purple" fontSize="2xs">{t('userActivity.badges.trial')}</Badge>}
    </HStack>
  ) : <Text color="gray.400">—</Text>;
};

const UserCell = ({ user }) => (
  user ? (
    <Box>
      <Text fontWeight="medium" noOfLines={1}>{user.name}</Text>
      <Text fontSize="xs" color="gray.500" noOfLines={1}>{user.role || '—'} · {user.email || user.username}</Text>
    </Box>
  ) : <Text color="gray.400">—</Text>
);

const LivePanel = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await activityService.live());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || t('userActivity.errors.loadLiveFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const tick = () => {
      if (!paused && document.visibilityState === 'visible') load();
    };
    tick();
    timer.current = setInterval(tick, LIVE_POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer.current);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load, paused]);

  const online = data?.online || [];
  const feed = data?.feed || [];
  const onlineHotels = new Set(online.map(o => o.hotel?.id).filter(Boolean)).size;
  const onlineTrials = new Set(online.filter(o => o.hotel?.is_trial).map(o => o.hotel.id)).size;

  return (
    <VStack align="stretch" spacing={4}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <SimpleGrid columns={{ base: 3 }} spacing={4} flex="1" maxW="560px">
          <Stat><StatLabel>{t('userActivity.live.onlineUsers')}</StatLabel><StatNumber>{online.length}</StatNumber></Stat>
          <Stat><StatLabel>{t('userActivity.live.hotelsActive')}</StatLabel><StatNumber>{onlineHotels}</StatNumber></Stat>
          <Stat><StatLabel>{t('userActivity.live.trialHotelsActive')}</StatLabel><StatNumber color="purple.500">{onlineTrials}</StatNumber></Stat>
        </SimpleGrid>
        <HStack>
          <Text fontSize="xs" color="gray.500">
            {paused ? t('userActivity.live.paused') : t('userActivity.live.refreshesEvery', { seconds: LIVE_POLL_MS / 1000 })}
            {data ? ` · ${t('userActivity.live.onlineWindow', { minutes: Math.round((data.online_window_s || 300) / 60) })}` : ''}
          </Text>
          <Tooltip label={paused ? t('userActivity.live.resume') : t('userActivity.live.pause')}>
            <IconButton size="sm" variant="outline" aria-label={t('userActivity.live.pauseLive')} icon={paused ? <FiPlay /> : <FiPause />}
                        onClick={() => setPaused(p => !p)} />
          </Tooltip>
          <IconButton size="sm" variant="outline" aria-label={t('userActivity.buttons.refresh')} icon={<FiRefreshCw />} isLoading={loading} onClick={load} />
        </HStack>
      </Flex>

      {error && <Text color="red.500" fontSize="sm">{error}</Text>}

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        <Card variant="outline">
          <CardHeader pb={2}><Heading size="sm">{t('userActivity.live.onlineNow')}</Heading></CardHeader>
          <CardBody pt={0} overflowX="auto">
            {!data ? <Spinner size="sm" /> : online.length === 0 ? (
              <Text fontSize="sm" color="gray.500">{t('userActivity.live.nobodyActive')}</Text>
            ) : (
              <Table size="sm">
                <Thead><Tr><Th>{t('userActivity.columns.user')}</Th><Th>{t('userActivity.columns.hotel')}</Th><Th>{t('userActivity.columns.on')}</Th><Th>{t('userActivity.columns.lastAction')}</Th><Th>{t('userActivity.columns.seen')}</Th></Tr></Thead>
                <Tbody>
                  {online.map(o => (
                    <Tr key={o.user?.id}>
                      <Td><UserCell user={o.user} /></Td>
                      <Td><HotelCell hotel={o.hotel} /></Td>
                      <Td>{o.page || '—'}</Td>
                      <Td>{o.last_action || '—'}</Td>
                      <Td whiteSpace="nowrap">{ago(o.seconds_ago, t)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        <Card variant="outline">
          <CardHeader pb={2}><Heading size="sm">{t('userActivity.live.liveFeed')}</Heading></CardHeader>
          <CardBody pt={0} maxH="420px" overflowY="auto">
            {!data ? <Spinner size="sm" /> : feed.length === 0 ? (
              <Text fontSize="sm" color="gray.500">{t('userActivity.live.noActivity')}</Text>
            ) : (
              <VStack align="stretch" spacing={2}>
                {feed.map((e, i) => (
                  <HStack key={`${e.ts}-${i}`} spacing={3} align="start" fontSize="sm">
                    <Text color="gray.500" minW="64px" whiteSpace="nowrap">{ago(e.seconds_ago, t)}</Text>
                    <Box flex="1">
                      <Text>
                        <b>{e.user?.name || t('userActivity.live.unknownUser')}</b>{' · '}{e.label}
                        {e.detail ? <Text as="span" color="gray.500"> ({e.detail})</Text> : null}
                      </Text>
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500">{e.hotel?.name}</Text>
                        {e.hotel?.is_trial && <Badge colorScheme="purple" fontSize="2xs">{t('userActivity.badges.trial')}</Badge>}
                      </HStack>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>
    </VStack>
  );
};

const HotelDetailDrawer = ({ hotelId, days, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !hotelId) return;
    setData(null);
    setError(null);
    activityService.hotel(hotelId, days).then(setData)
      .catch(e => setError(e?.response?.data?.error || e.message || t('userActivity.errors.loadFailed')));
  }, [isOpen, hotelId, days, t]);

  const maxDaily = Math.max(1, ...(data?.daily || []).map(d => d.events));

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          {data ? <HotelCell hotel={data.hotel} /> : t('userActivity.drawer.title')}
          {data && (
            <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
              {[data.hotel.city, data.hotel.country].filter(Boolean).join(', ')} · {data.hotel.email} ·
              {' '}{t('userActivity.hotels.roomCount', { count: data.hotel.rooms })} · {t('userActivity.drawer.created', { date: fmtDate(data.hotel.created_at) })} ·
              {' '}{data.hotel.onboarding_completed ? t('userActivity.drawer.onboardingDone') : t('userActivity.drawer.onboardingNotFinished')}
            </Text>
          )}
        </DrawerHeader>
        <DrawerBody>
          {error && <Text color="red.500">{error}</Text>}
          {!data && !error && <Spinner />}
          {data && (
            <VStack align="stretch" spacing={6} py={2}>
              <Box>
                <Heading size="xs" mb={2}>{t('userActivity.drawer.dailyActivity', { count: data.days })}</Heading>
                {data.daily.length === 0 ? <Text fontSize="sm" color="gray.500">{t('userActivity.drawer.noActivity')}</Text> : (
                  <HStack align="end" spacing="2px" h="80px">
                    {data.daily.map(d => (
                      <Tooltip key={d.date} label={`${d.date}: ${d.events}`}>
                        <Box flex="1" minW="4px" bg="blue.400" borderRadius="sm" h={`${(d.events / maxDaily) * 100}%`} />
                      </Tooltip>
                    ))}
                  </HStack>
                )}
              </Box>
              <Box>
                <Heading size="xs" mb={2}>{t('userActivity.drawer.users')}</Heading>
                <Table size="sm">
                  <Thead><Tr><Th>{t('userActivity.columns.user')}</Th><Th isNumeric>{t('userActivity.columns.events')}</Th><Th isNumeric>{t('userActivity.columns.days')}</Th><Th>{t('userActivity.columns.mostly')}</Th></Tr></Thead>
                  <Tbody>
                    {data.users.map(u => (
                      <Tr key={u.user?.id}>
                        <Td><UserCell user={u.user} /></Td>
                        <Td isNumeric>{u.events}</Td>
                        <Td isNumeric>{u.active_days}</Td>
                        <Td fontSize="xs">{u.top_actions.map(a => `${a.label} ×${a.count}`).join(', ')}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              <Box>
                <Heading size="xs" mb={2}>{t('userActivity.drawer.actionsAndPages')}</Heading>
                <Wrap>
                  {data.actions.map(a => (
                    <WrapItem key={a.key}><Badge variant="subtle" textTransform="none">{a.label} · {a.count}</Badge></WrapItem>
                  ))}
                </Wrap>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

const HotelsPanel = () => {
  const { t } = useTranslation();
  const [days, setDays] = useState(7);
  const [trialsOnly, setTrialsOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const drawer = useDisclosure();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await activityService.hotels(days));
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || t('userActivity.errors.loadHotelsFailed'));
    } finally {
      setLoading(false);
    }
  }, [days, t]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.hotels || []).filter(r =>
      (!trialsOnly || r.hotel.is_trial) &&
      (!q || [r.hotel.name, r.hotel.city, r.hotel.country, r.hotel.email].some(v => (v || '').toLowerCase().includes(q))));
  }, [data, trialsOnly, search]);

  return (
    <Card variant="outline">
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <Box>
            <Heading size="sm">{t('userActivity.hotels.title')}</Heading>
            <Text fontSize="xs" color="gray.500">
              {t('userActivity.hotels.scoreExplanation', { score: HOT_LEAD_SCORE })}
            </Text>
          </Box>
          <HStack spacing={4} wrap="wrap">
            <Input size="sm" placeholder={t('userActivity.hotels.searchPlaceholder')} maxW="220px" value={search} onChange={e => setSearch(e.target.value)} />
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel htmlFor="trials-only" mb={0} fontSize="sm">{t('userActivity.hotels.trialsOnly')}</FormLabel>
              <Switch id="trials-only" isChecked={trialsOnly} onChange={e => setTrialsOnly(e.target.checked)} />
            </FormControl>
            <Select size="sm" w="auto" value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={1}>{t('userActivity.hotels.periodToday')}</option>
              <option value={7}>{t('userActivity.hotels.periodLastDays', { count: 7 })}</option>
              <option value={30}>{t('userActivity.hotels.periodLastDays', { count: 30 })}</option>
              <option value={90}>{t('userActivity.hotels.periodLastDays', { count: 90 })}</option>
            </Select>
            <IconButton size="sm" variant="outline" aria-label={t('userActivity.buttons.refresh')} icon={<FiRefreshCw />} isLoading={loading} onClick={load} />
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody pt={0} overflowX="auto">
        {error && <Text color="red.500" fontSize="sm">{error}</Text>}
        {!data ? <Spinner size="sm" /> : rows.length === 0 ? (
          <Text fontSize="sm" color="gray.500">{t('userActivity.hotels.noActivity')}</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('userActivity.columns.hotel')}</Th><Th isNumeric>{t('userActivity.columns.score')}</Th><Th isNumeric>{t('userActivity.columns.users')}</Th><Th isNumeric>{t('userActivity.columns.days')}</Th>
                <Th isNumeric>{t('userActivity.columns.events')}</Th><Th>{t('userActivity.columns.topActions')}</Th><Th>{t('userActivity.columns.lastActive')}</Th><Th>{t('userActivity.columns.created')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map(r => (
                <Tr key={r.hotel.id} cursor="pointer" _hover={{ bg: 'gray.50' }}
                    onClick={() => { setSelected(r.hotel.id); drawer.onOpen(); }}>
                  <Td>
                    <HotelCell hotel={r.hotel} />
                    <Text fontSize="xs" color="gray.500">
                      {[r.hotel.city, r.hotel.country].filter(Boolean).join(', ')} · {t('userActivity.hotels.roomCount', { count: r.hotel.rooms })}
                      {!r.hotel.onboarding_completed ? ` · ${t('userActivity.hotels.onboardingOpen')}` : ''}
                    </Text>
                  </Td>
                  <Td isNumeric>
                    <HStack justify="end" spacing={2}>
                      {r.hotel.is_trial && r.score >= HOT_LEAD_SCORE && <Badge colorScheme="orange">{t('userActivity.badges.lead')}</Badge>}
                      <Text fontWeight="semibold">{r.score}</Text>
                    </HStack>
                  </Td>
                  <Td isNumeric>{r.active_users}</Td>
                  <Td isNumeric>{r.active_days}</Td>
                  <Td isNumeric>{r.events}</Td>
                  <Td>
                    <Wrap spacing={1}>
                      {r.top_actions.slice(0, 4).map(a => (
                        <WrapItem key={a.key}><Badge variant="subtle" textTransform="none" fontSize="2xs">{a.label} {a.count}</Badge></WrapItem>
                      ))}
                    </Wrap>
                  </Td>
                  <Td whiteSpace="nowrap">{fmtDate(r.last_active)}</Td>
                  <Td whiteSpace="nowrap">{fmtDate(r.hotel.created_at)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </CardBody>
      <HotelDetailDrawer hotelId={selected} days={Math.max(days, 30)} isOpen={drawer.isOpen} onClose={drawer.onClose} />
    </Card>
  );
};

/**
 * Superadmin: who is using the product right now, and which hotels
 * (especially trials) are engaged. Tracks a fixed set of key actions and
 * pages (backend activity/catalog.py), not every request.
 */
const UserActivity = () => {
  const { t } = useTranslation();
  return (
    <PageShell
      title={t('userActivity.title')}
      subtitle={t('userActivity.subtitle')}
    >
      <VStack align="stretch" spacing={6}>
        <LivePanel />
        <HotelsPanel />
      </VStack>
    </PageShell>
  );
};

export default UserActivity;
